import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    TextField, 
    Grid, 
    Card, 
    CardContent,
    FormHelperText,
    Paper,
    Divider,
    Checkbox,
    FormControlLabel,
    IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete } from '@react-google-maps/api';

/**
 * Address Information step of venue onboarding
 * Collects various address types and special instructions
 */
export const AddressInformationStep = ({ 
    mainAddress,
    setMainAddress,
    city,
    setCity,
    state,
    setState,
    zipCode,
    setZipCode,
    country,
    setCountry,
    neighborhood,
    setNeighborhood,
    deliveryAddress,
    setDeliveryAddress,
    serviceEntryAddress,
    setServiceEntryAddress,
    parkingInstructions,
    setParkingInstructions,
    mailingAddress,
    setMailingAddress,
    addressPhotos,
    setAddressPhotos,
    specialInstructions,
    setSpecialInstructions,
    errors
}) => {
    // Local state to track if delivery/service/mailing address is same as main address
    const [useMainForDelivery, setUseMainForDelivery] = useState(false);
    const [useMainForService, setUseMainForService] = useState(false);
    const [useMainForMailing, setUseMainForMailing] = useState(false);
    const [autocomplete, setAutocomplete] = useState(null);

    // Handle address photo upload
    const handleAddressPhotoChange = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setAddressPhotos([...addressPhotos, ...filesArray]);
        }
    };

    // Handle place changed in Google Maps Autocomplete
    const handlePlaceChanged = () => {
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();

            if (place.formatted_address) {
                setMainAddress(place.formatted_address);
                
                // If checkboxes are checked, update those addresses too
                if (useMainForDelivery) {
                    setDeliveryAddress(place.formatted_address);
                }
                if (useMainForService) {
                    setServiceEntryAddress(place.formatted_address);
                }
                if (useMainForMailing) {
                    setMailingAddress(place.formatted_address);
                }
                
                // Extract address components
                if (place.address_components) {
                    // Extract city, state, zip, country from address components
                    place.address_components.forEach(component => {
                        const types = component.types;
                        
                        if (types.includes('locality')) {
                            setCity(component.long_name);
                        } else if (types.includes('administrative_area_level_1')) {
                            setState(component.long_name);
                        } else if (types.includes('postal_code')) {
                            setZipCode(component.long_name);
                        } else if (types.includes('country')) {
                            setCountry(component.long_name);
                        } else if (types.includes('neighborhood') || types.includes('sublocality')) {
                            setNeighborhood(component.long_name);
                        }
                    });
                }
            } else if (place.name) {
                // Sometimes place only returns a name
                setMainAddress(place.name);
                
                // Update other addresses if using main address
                if (useMainForDelivery) {
                    setDeliveryAddress(place.name);
                }
                if (useMainForService) {
                    setServiceEntryAddress(place.name);
                }
                if (useMainForMailing) {
                    setMailingAddress(place.name);
                }
            }
        }
    };

    // Remove address photo by index
    const handleRemoveAddressPhoto = (index) => {
        const updatedPhotos = [...addressPhotos];
        updatedPhotos.splice(index, 1);
        setAddressPhotos(updatedPhotos);
    };

    // Handle delivery address checkbox
    const handleDeliveryCheckbox = (e) => {
        setUseMainForDelivery(e.target.checked);
        if (e.target.checked) {
            setDeliveryAddress(mainAddress);
        } else {
            setDeliveryAddress('');
        }
    };

    // Handle service entry checkbox
    const handleServiceCheckbox = (e) => {
        setUseMainForService(e.target.checked);
        if (e.target.checked) {
            setServiceEntryAddress(mainAddress);
        } else {
            setServiceEntryAddress('');
        }
    };

    // Handle mailing address checkbox
    const handleMailingCheckbox = (e) => {
        setUseMainForMailing(e.target.checked);
        if (e.target.checked) {
            setMailingAddress(mainAddress);
        } else {
            setMailingAddress('');
        }
    };

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Address Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Provide details about your venue's location and access information
                </Typography>
                
                <Grid container spacing={3}>
                    {/* Main Address Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Main Venue Address <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Autocomplete
                            onLoad={setAutocomplete}
                            onPlaceChanged={handlePlaceChanged}
                        >
                            <TextField
                                fullWidth
                                label="Venue Address"
                                value={mainAddress}
                                onChange={(e) => setMainAddress(e.target.value)}
                                required
                                error={!!errors.mainAddress}
                                helperText={errors.mainAddress || "Full address of the venue"}
                                placeholder="Start typing your venue address..."
                            />
                        </Autocomplete>
                    </Grid>
                    
                    {/* City, State, Zip, Country Section */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            required
                            error={!!errors.city}
                            helperText={errors.city || "City"}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="State/Province"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            required
                            error={!!errors.state}
                            helperText={errors.state || "State or province"}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Postal Code"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            required
                            error={!!errors.zipCode}
                            helperText={errors.zipCode || "ZIP or postal code"}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Country"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            required
                            error={!!errors.country}
                            helperText={errors.country || "Country"}
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Neighborhood"
                            value={neighborhood}
                            onChange={(e) => setNeighborhood(e.target.value)}
                            helperText="Neighborhood or district (optional)"
                        />
                    </Grid>
                    
                    {/* Delivery Address Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Delivery Address
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={useMainForDelivery}
                                    onChange={handleDeliveryCheckbox}
                                />
                            }
                            label="Same as main address"
                            sx={{ mb: 1 }}
                        />
                        {!useMainForDelivery && (
                            <TextField
                                fullWidth
                                label="Delivery Address"
                                value={deliveryAddress}
                                onChange={(e) => setDeliveryAddress(e.target.value)}
                                disabled={useMainForDelivery}
                                helperText="Address for deliveries if different from the main address"
                                placeholder="Where should vendors deliver items?"
                            />
                        )}
                    </Grid>
                    
                    {/* Service Entry Address Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Service Entry Address
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={useMainForService}
                                    onChange={handleServiceCheckbox}
                                />
                            }
                            label="Same as main address"
                            sx={{ mb: 1 }}
                        />
                        {!useMainForService && (
                            <TextField
                                fullWidth
                                label="Service Entry Address"
                                value={serviceEntryAddress}
                                onChange={(e) => setServiceEntryAddress(e.target.value)}
                                disabled={useMainForService}
                                helperText="Address for service providers to enter, if different"
                                placeholder="Where should caterers and other service providers enter?"
                            />
                        )}
                    </Grid>
                    
                    {/* Mailing Address Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Mailing Address
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={useMainForMailing}
                                    onChange={handleMailingCheckbox}
                                />
                            }
                            label="Same as main address"
                            sx={{ mb: 1 }}
                        />
                        {!useMainForMailing && (
                            <TextField
                                fullWidth
                                label="Mailing Address"
                                value={mailingAddress}
                                onChange={(e) => setMailingAddress(e.target.value)}
                                disabled={useMainForMailing}
                                helperText="Address for mail and correspondence, if different"
                                placeholder="Where should mail be sent?"
                            />
                        )}
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                    </Grid>
                    
                    {/* Parking Instructions Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Parking Instructions
                        </Typography>
                        <TextField
                            fullWidth
                            label="Parking Instructions"
                            value={parkingInstructions}
                            onChange={(e) => setParkingInstructions(e.target.value)}
                            multiline
                            rows={3}
                            helperText="Information about parking availability, access, and any associated costs"
                            placeholder="Details about parking options, availability, fees, etc."
                        />
                    </Grid>
                    
                    {/* Special Instructions Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Special Access Instructions
                        </Typography>
                        <TextField
                            fullWidth
                            label="Special Instructions"
                            value={specialInstructions}
                            onChange={(e) => setSpecialInstructions(e.target.value)}
                            multiline
                            rows={3}
                            helperText="Any special instructions for accessing the venue (e.g., security gate codes, loading dock information, etc.)"
                            placeholder="Details about gates, codes, elevators, loading docks, etc."
                        />
                    </Grid>
                    
                    {/* Address Photos Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Location & Access Photos
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Upload photos that help identify your venue from the outside or show access points (optional but recommended)
                        </Typography>
                        
                        {addressPhotos.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {addressPhotos.map((photo, index) => (
                                    <Box 
                                        key={index} 
                                        sx={{ 
                                            position: 'relative',
                                            width: 150,
                                            height: 120,
                                            borderRadius: 1,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ 
                                                position: 'absolute', 
                                                top: 5, 
                                                right: 5,
                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.7)'
                                                }
                                            }}
                                            onClick={() => handleRemoveAddressPhoto(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                        <img 
                                            src={URL.createObjectURL(photo)} 
                                            alt={`Address Photo ${index}`} 
                                            style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' 
                                            }} 
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            sx={{ mt: 1 }}
                        >
                            Upload Photos
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleAddressPhotoChange}
                            />
                        </Button>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            Photos of your entrance, building exterior, parking area, loading docks, etc.
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default AddressInformationStep;
